import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { format } from 'date-fns'
import { getDateFnsLocale } from '@vacationtracker/shared/i18n'
import { App, Form, Button, Row, Col, Switch, Typography, Divider } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { loadStripe } from '@stripe/stripe-js'
import { useStripe, useElements, CardElement, Elements } from '@stripe/react-stripe-js'
import { useManualQuery } from 'graphql-hooks'
import isNumber from 'lodash/isNumber'

import Api from '@vacationtracker/shared/services/api'
import { getCompanyInfo, getCompanyBillingInfoForDowngrade, getWorkWeekList } from '../../graphql/custom-queries'
import * as logger from '../../services/logger'
import { track } from '../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../util/analytics-helpers'
import { wait } from '@vacationtracker/shared/functions/wait'
import { ProductService } from '@vacationtracker/shared/data/stripe/product-service'
import { IPrice, PriceName } from '@vacationtracker/shared/data/stripe/prices'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { selectAuthCompanySlice, setAuthCompany } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectLocaleSlice } from '../../store/locale-slice'
import { notificationStore } from '../../context/notificationsContext/store'
import { logout } from '../../services/auth/logout-handler'
import { openSupportChat } from '../../util/open-support-chat'
import { calculateFreemiumRestrictions, IPlanDowngradeSourceData } from '../../util/calculate-freemium-restrictions'

import IntlMessages from '../../util/IntlMessages'
import CircularProgress from '../../components/circular-progress'
import BillingForm from '../../components/billing-form'
import PricePlanCard from '../../components/price-plan-card'
import BillingCouponCode from '../../components/billing-coupon-code'

import { PlanDowngradeModal } from '../../components/plan-downgrade-modal'
import { BillingTypesEnum, IBillingAddress, SubscriptionPeriod, USER_PLAN_LIMIT } from '@vacationtracker/shared/types/billing'
import { IGetCompanyBillingInfoDataForDowngrade } from '../../types/company'
import { SubscriptionPlan, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { IGetCompanyInfoData } from '../../types/custom-queries'
import { ICompanyWorkWeek, IGetCompanyWorkWeeks } from '../../types/locations'

import { FrontendUrls } from '../../types/urls'
import { ADPMarketplaceBtn } from '../../components/adp/marketplace-btn'

// Make sure to call `loadStripe` outside of a component’s render to avoid recreating the `Stripe` object on every render.
const stripePromise = process.env.REACT_APP_STRIPE_KEY ? loadStripe(process.env.REACT_APP_STRIPE_KEY) : Promise.reject('No Stripe Key')

const { Paragraph, Title } = Typography

const CheckoutForm = () => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  const showFreemiumPlan = true
  const [numberOfSeats, setNumberOfSeats] = useState<number | undefined>(undefined)
  const productService = new ProductService(process.env.REACT_APP_ENVIRONMENT === 'production' ? 'prod' : 'test')
  const currentPrices = productService.getCurrentPrices()

  const history = useHistory()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { notification } = App.useApp()

  const [isLoading, setIsLoading] = useState(true)
  const [submitLoader, setSubmitLoader] = useState(false)
  const [stripeCustomerId, setStripeCustomerId] = useState('')
  const [billingType, setBillingType] = useState<BillingTypesEnum>(BillingTypesEnum.payAsYouGo)
  const [seats, setSeats] = useState<number | null>(null)

  // Store source data for plan downgrade restrictions computation
  const [planDowngradeSourceData, setPlanDowngradeSourceData] = useState<IPlanDowngradeSourceData | null>(null)

  // Compute plan downgrade restrictions from source data (memoized for performance)
  const freemiumRestrictions = useMemo(
    () => calculateFreemiumRestrictions(planDowngradeSourceData),
    [planDowngradeSourceData]
  )
  const [customerPortalLink, setCustomerPortalLink] = useState<string | undefined>()
  const [correlationId, setCorrelationId] = useState('')
  const [newPlan, setNewPlan] = useState<SubscriptionPlan>('Core')
  const [currentPeriod, setCurrentPeriod] = useState<SubscriptionPeriod>('monthly')
  const [newPeriod, setNewPeriod] = useState<SubscriptionPeriod>('monthly')
  const [numberOfUsers, setNumberOfUsers] = useState<number>(0)
  const [showPlans, setShowPlans] = useState<boolean>(false)
  const [isAdp, setIsAdp] = useState<boolean>(false)
  const [isTrialPeriod, setIsTrialPeriod] = useState(false)

  const [stripePaymentStatus, setStripePaymentStatus] = useState<string>('')
  const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan>('Core')
  const [skipLoadingScroll, setSkipLoadingScroll] = useState<boolean>(true)

  // Entity selection modal state
  const [showEntitySelectionModal, setShowEntitySelectionModal] = useState(false)
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | null>(null)
  const [stripeCurrentPeriodEnd, setStripeCurrentPeriodEnd] = useState<string>('')

  const stripe = useStripe()
  const elements = useElements()

  const [ getCompanyBillingInfoQuery ] = useManualQuery<IGetCompanyBillingInfoDataForDowngrade>(getCompanyBillingInfoForDowngrade)
  const [ getCompanyInfoQuery ] = useManualQuery<IGetCompanyInfoData>(getCompanyInfo)
  const [ getWorkWeekListQuery ] = useManualQuery<IGetCompanyWorkWeeks>(getWorkWeekList)

  const billingInfoRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    track('RESUBSCRIPTION_PAGE_VIEWED', {})
    fetchBilling()
  }, [])

  useEffect(() => {
    if (Array.isArray(actionNotifications) &&
      (
        correlationId &&
        !actionNotifications.includes(correlationId)
      )) {
      getCompanyData()
    }
  }, [actionNotifications])

  useEffect(() => {
    if (authCompany && ['active', 'trialing'].includes(authCompany.subscriptionStatus)) {
      history.replace(FrontendUrls.dashboard)
    }
  }, [authCompany])

  useEffect(() => {
    if (stripeCustomerId) {
      getCustomerPortalLink()
        .then((link: string) => {
          setCustomerPortalLink(link)
        })
    }
  }, [stripeCustomerId])

  useEffect(() => {
    if (skipLoadingScroll) return

    if (billingInfoRef.current) {
      billingInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [newPlan])

  const fetchBilling = async () => {
    let workWeekList: ICompanyWorkWeek[] = []
    try {
      // Fetch work week list separately (same as BillingPage)
      const workWeekListResponse = await getWorkWeekListQuery()
      workWeekList = workWeekListResponse.data?.getWorkWeekList || []
    } catch (e) {
      // If this fails, continue without workWeek data (validation will be skipped)
      logger.error('error fetching work week list', e)
    }

    try {
      const response = await getCompanyBillingInfoQuery()
      if (!response.data || response.error) throw response.error
      const billing = response.data.getCompany.billing

      setNumberOfUsers(response.data.getCompany.numberOfActiveUsers || 1)
      if (billing.billingType === BillingTypesEnum.seatBased) {
        isNumber(billing.seats) && setNumberOfSeats(billing.seats)
      }
      setSubscriptionPlan(billing.subscriptionPlan)
      setIsTrialPeriod(response.data.getCompany.trialPeriod === 'in_trial' ? true : false)

      setStripePaymentStatus(billing.stripePaymentStatus)
      setCurrentPeriod(billing.nextSubscriptionPeriod || billing.subscriptionPeriod || 'monthly')
      setNewPlan(billing.nextSubscriptionPlan || billing.subscriptionPlan || 'Core')
      setNewPeriod(billing.nextSubscriptionPeriod || billing.subscriptionPeriod || 'monthly')
      setStripeCurrentPeriodEnd(billing.stripeCurrentPeriodEnd || '')
      setSubmitLoader(false)

      // Store source data for plan downgrade restrictions (will be computed via useMemo)
      setPlanDowngradeSourceData({
        plan: response.data.getCompany.plan,
        teams: response.data.getTeamList || [],
        locations: response.data.getLocationList || [],
        leaveTypes: response.data.getLeaveTypeList || [],
        workWeeks: workWeekList,
      })

      if (billing) {
        form.setFieldsValue({
          billingName: billing.billingName,
          billingEmails: billing.billingEmails,
          city: billing.address && billing.address.city,
          country: billing.address && billing.address.country,
          state: billing.address && billing.address.state,
          address: billing.address && billing.address.address,
          addressLine2: billing.address && billing.address.addressLine2,
        })
        setIsAdp(billing.paymentProcessor === 'adp')
      }

      setStripeCustomerId(billing?.stripeCustomerId)
      setBillingType(billing?.billingType || BillingTypesEnum.payAsYouGo)
      setSeats(billing?.seats || null)
      setIsLoading(false)
    } catch (err) {
      logger.error('error fetching billing', err)
      setSubmitLoader(false)
    }
  }

  const submit = async () => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    try {
      const values = await form.validateFields()
      setSubmitLoader(true)

      let stripePaymentMethodId

      if (!values.sendInvoice) {
        const cardElement = elements.getElement(CardElement)

        if (!cardElement) {
          return
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        })

        if (error || !paymentMethod) {
          logger.error(error)
          setSubmitLoader(false)
          throw error
        } else {
          logger.error('[PaymentMethod]', paymentMethod)
        }

        stripePaymentMethodId = paymentMethod.id
      }

      const address: IBillingAddress = {
        city: values.city,
        country: values.country,
        state: values.state,
        address: values.address,
      }
      if (values.addressLine2) {
        address.addressLine2 = values.addressLine2
      }

      const response = await Api.post('/core/event', {
        eventType: 'BILLING_UPDATED',
        eventGroup: 'BILLING',
        paymentProcessor: 'stripe',
        billingName: values.billingName,
        billingEmails: values.billingEmails,
        billingType,
        seats: billingType === BillingTypesEnum.payAsYouGo ? undefined : seats,
        address,
        stripePaymentMethodId,
        stripeCouponId: values.promoCode,
        sendInvoice: values.sendInvoice,
        subscriptionPlan: newPlan,
        subscriptionPeriod: newPeriod,
        keepLegacyPlan: false,
        activeUsers: numberOfUsers,
        ...(values.promoCode && { stripeCouponId: values.promoCode }),
      })

      track('RESUBSCRIPTION_PAGE_SUBMIT_BUTTON_CLICKED', {})
      track('CARD_ADDED_TOTAL', {})

      setCorrelationId(response.correlationId)

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: newPlan === 'Freemium' ? 'subscription.downgradeInProgress' : 'subscription.updateInProgress' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])

    } catch (error) {
      if (error.message) {
        notification.error({
          message: error.message,
        })
      } else {
        notification.error({
          message: JSON.stringify(error) ? JSON.stringify(error) : error,
        })
      }
      setSubmitLoader(false)
    }
  }

  const getCustomerPortalLink = async (): Promise<string> => {
    const portalSession = await Api.post('/stripe/create-customer-portal-session', {
      stripeCustomerId,
      redirectUrl: window.location.href,
    })

    return portalSession.url
  }

  const onLogout = () => {
    logout({
      history,
      reduxDispatch: dispatch,
      userId: authUser.id,
    })
  }

  const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  }

  let numberOfRetry = 1
  const getCompanyData = async () => {
    try {
      const response = await getCompanyInfoQuery()
      if (!response.data || response.error) throw response.error
      if (response.data.getCompany.subscriptionStatus !== 'active' && numberOfRetry < 8) {
        numberOfRetry++
        await wait(200 * numberOfRetry)
        return await getCompanyData()
      }
      if (numberOfRetry >= 8) {
        throw new Error('Get company data failed')
      }
      track('PLAN_SWITCH_SUCCESS', {
        ...getAnalyticsProperties(),
        plan: response.data.getCompany.plan,
        source: 'resubscribe_page',
      })
      dispatch(setAuthCompany(response.data.getCompany))
    } catch (err) {
      setSubmitLoader(false)
      logger.error('ERROR GET COMPANY DATA', err)
    }
  }

  const changePeriod = (period) => {
    setNewPeriod(period ? 'annual' : 'monthly')
  }

  const changePlanHandler = (plan: SubscriptionPlan) => {
    track('PLAN_SWITCH_BUTTON_CLICKED', {
      ...getAnalyticsProperties(),
      plan,
      source: 'resubscribe_page',
    })
    
    // Check if entity selection modal is needed for downgrade
    const isDowngradeToFreemium = plan === 'Freemium' && (subscriptionPlan === 'Core' || subscriptionPlan === 'Complete')
    const isDowngradeToCore = plan === 'Core' && subscriptionPlan === 'Complete'

    const needsModal =
      isDowngradeToFreemium ||  // Always show modal for Freemium downgrades (for limitations info, timing, confirmation)
      isDowngradeToCore  // Always show modal for Complete → Core (for timing, probation period, confirmation)

    if (needsModal) {
      setPendingPlan(plan)
      setShowEntitySelectionModal(true)
    } else {
      setNewPlan(plan)
    }
  }

  const handleEntitySelectionSubmit = async (selections: unknown) => {
    // The modal handles sending deletion/disable events internally
    // After modal completes, we need to:
    // 1. Wait for backend to process those deletions (important!)
    // 2. Then send BILLING_UPDATED event (which has validation checks)
    // Delay before sending BILLING_UPDATED after entity deletions to ensure backend has processed all deletion events
    // Increased to 6 seconds to handle companies with many entities (leave types, locations, departments)

    if (!pendingPlan) {
      return Promise.resolve()
    }

    const planToSwitch = pendingPlan
    setNewPlan(pendingPlan)
    setShowEntitySelectionModal(false)
    setPendingPlan(null)

    // For Freemium (no payment method needed), send billing update after deletions are processed
    if (planToSwitch === 'Freemium') {
      try {
        setSubmitLoader(true)

        // CRITICAL: Wait for backend to process deletion/disable events
        // Backend has validation checks that verify entity limits
        // If we send BILLING_UPDATED too quickly, validation will fail

        // Get billing info from form if available
        const billingValues = form.getFieldsValue()

        // Build address object only if fields have values
        // For Freemium, address is optional in the schema, so don't send empty address
        let address: IBillingAddress | undefined
        if (billingValues.city && billingValues.country && billingValues.address) {
          address = {
            city: billingValues.city,
            country: billingValues.country,
            state: billingValues.state || '',
            address: billingValues.address,
          }
          if (billingValues.addressLine2) {
            address.addressLine2 = billingValues.addressLine2
          }
        }

        // Now send billing update event
        const response = await Api.post('/core/event', {
          eventType: 'BILLING_UPDATED',
          eventGroup: 'BILLING',
          paymentProcessor: 'stripe',
          billingName: billingValues.billingName || '',
          billingEmails: billingValues.billingEmails || [],
          billingType,
          seats: billingType === BillingTypesEnum.payAsYouGo ? undefined : seats,
          ...(address && { address }),
          subscriptionPlan: planToSwitch,
          subscriptionPeriod: newPeriod,
          keepLegacyPlan: false,
          activeUsers: numberOfUsers,
        })

        track('RESUBSCRIPTION_PAGE_SUBMIT_BUTTON_CLICKED', {})

        setCorrelationId(response.correlationId)

        notification.open({
          key: response.correlationId,
          message: formatMessage({ id: 'subscription.downgradeInProgress' }),
          icon: <LoadingOutlined />,
          duration: 0,
        })
        setActionNotifications([...actionNotifications, response.correlationId])
      } catch (error) {
        setSubmitLoader(false)
        const errorMessage = error instanceof Error ? error.message : String(error)
        notification.error({
          message: errorMessage,
        })
      }
    }

    return Promise.resolve()
  }

  const handleEntitySelectionCancel = () => {
    setShowEntitySelectionModal(false)
    setPendingPlan(null)
  }

  return (
    <section className="ant-layout ant-layout-has-sider app-layout">
      <section className="ant-layout content-layout">
        <main className="ant-layout-content">
          <div className="main-content-wrapper" style={{ padding: '10px' }}>
            <div className="main-content form-background" style={{ padding: '0px' }}>
              <div className="main-content-wrapper subscription-wrapper">
                <div className="main-content-header form-layout">
                  <div className="main-content-header-title">
                    <span><IntlMessages id="subscription.title" /></span>
                  </div>
                  <div className="ant-card-extra">
                    <Button type="text" onClick={() => onLogout()}><IntlMessages id="app.logout" /></Button>
                  </div>
                </div>
                <div className="main-content-body form-layout">
                  <Paragraph>
                    <IntlMessages
                      id="billing.subscriptionExpiredText"
                      values={{
                        supportLink: <a onClick={() => openSupportChat()}><IntlMessages id="components.helpDrawer.supportTitle" /></a>,
                      }}
                    />
                  </Paragraph>
                  <Divider />
                  {!isAdp && <div className="billing-body form-layout">
                    {isLoading ? <CircularProgress /> :
                      <Form
                        form={form}
                        layout="horizontal"
                        name="billingForm"
                        size="large"
                        onFinish={submit}
                        {...layout}
                      >
                        <Row gutter={{
                          xs: 8,
                          sm: 16,
                          md: 24,
                          lg: 32,
                        }}>
                          <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                            <Title level={4}>
                              <IntlMessages id="components.billingForm.planTitle" />
                            </Title>
                            <Paragraph type="secondary">
                              <IntlMessages id="billing.pricingPlansText" />
                            </Paragraph>
                          </Col>
                          <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                            <Form.Item label={<IntlMessages id="billing.SubscriptionPlan" />} colon={false}>
                              <div className='switch-plan-wrapper'>
                                <div className='wrapper-plan-vt'>
                                  <Paragraph>
                                    {(() => {
                                      if (newPlan === 'Freemium') {
                                        return <Title level={4}><IntlMessages id="app.freemium" /></Title>
                                      }
                                      const cardPrice = currentPrices.find(p => p.plan === newPlan && p.period === newPeriod) as IPrice
                                      const effectiveUsers = Math.max(numberOfUsers, USER_PLAN_LIMIT)
                                      const total = cardPrice.pricePerUser * effectiveUsers
                                      return <>
                                        <Title level={4}>{newPlan}</Title>
                                        <Paragraph>${total} <IntlMessages id={newPeriod === 'annual' ? 'app.billing.periodAnnual' : 'app.monthly'} /></Paragraph>
                                      </>
                                    })()}
                                  </Paragraph>
                                  <Button size="large" type="primary"  disabled={isLoading} onClick={() => {
                                    track('SHOW_PLANS_CLICKED', {
                                      ...getAnalyticsProperties(),
                                      plan: newPlan,
                                      source: 'resubscribe_page',
                                    })
                                    setSkipLoadingScroll(false)
                                    setShowPlans(!showPlans)
                                  }} ghost>
                                    <IntlMessages id={showPlans ? 'subscription.hidePlansButton' : 'subscription.showPlansButton'} />
                                  </Button>
                                </div>
                              </div>
                            </Form.Item>
                          </Col>
                        </Row>

                        {showPlans && <>
                          <Row justify={showFreemiumPlan ? 'center' : 'end'} gutter={{
                            xs: 8,
                            sm: 16,
                            md: 24,
                            lg: 32,
                          }} style={{ marginBottom: 20 }}>
                            <Col
                              xl={showFreemiumPlan ? 32 : 16}
                              xs={showFreemiumPlan ? 32 : 24}
                            >
                              <div className="plan-period-switch">
                                <IntlMessages id="app.monthly" />
                                <Switch
                                  style={{ backgroundColor: 'grey' }}
                                  checked={newPeriod === 'annual'}
                                  onChange={changePeriod}
                                />
                                <IntlMessages id="app.billing.periodAnnual" />
                              </div>
                            </Col>
                          </Row>

                          <Row justify={showFreemiumPlan ? 'center' : 'end'} style={{ marginBottom: 10 }} gutter={{ lg: 16, xl: numberOfUsers > 300 ? 4 : 8 }}>
                            {showFreemiumPlan && (
                              <Col xxl={{ span: 8, offset: 0 }} xl={8} lg={12} md={12} sm={24} xs={24} style={{ display: 'flex' }}>
                                <PricePlanCard
                                  plan={SubscriptionPlanEnum.freemium}
                                  price={productService.getPriceByName(PriceName.FREEMIUM_2025)}
                                  totalUsers={billingType === BillingTypesEnum.seatBased && isNumber(numberOfSeats) ? numberOfSeats : numberOfUsers}
                                  currentPeriod={currentPeriod}
                                  newPeriod={newPeriod}
                                  currentPlan={newPlan}
                                  showRevertingInfo={true}
                                  isSignup={false}
                                  isResubscribe={true}
                                  onSelectPLan={changePlanHandler}
                                  isTrialPeriod={isTrialPeriod}
                                  source="resubscribe_page"
                                  forceDisable={stripePaymentStatus === 'past_due'}
                                  billingType={billingType}
                                  disableLimitTeam={freemiumRestrictions.disableTeamsLimit}
                                  disableLimitLocation={freemiumRestrictions.disableLocationsLimit}
                                  disableLimitSubdepartments={freemiumRestrictions.disableSubdepartmentsLimit}
                                  disableMultiLevelApproval={freemiumRestrictions.disableMultiLevelApproval}
                                  disableNotificationWatchers={freemiumRestrictions.disableNotificationWatchers}
                                  disableLimitLeaveTypes={freemiumRestrictions.disableLeaveTypesLimit}
                                  totalSubdeparments={freemiumRestrictions.totalSubdepartments}
                                  disableLimitWorkHours={freemiumRestrictions.disableCustomWorkWeek}
                                />
                              </Col>
                            )}
                            <Col xxl={{ span: 8, offset: 0 }} xl={8} lg={12} md={12} sm={24} xs={24} style={{ display: 'flex' }}>
                              <PricePlanCard
                                plan='Core'
                                price={currentPrices.find(p => p.plan === 'Core' && p.period === newPeriod) as IPrice}
                                totalUsers={numberOfUsers}
                                currentPeriod={currentPeriod}
                                newPeriod={newPeriod}
                                currentPlan={newPlan}
                                showRevertingInfo={true}
                                isSignup={false}
                                isResubscribe={true}
                                onSelectPLan={() => changePlanHandler('Core')}
                                isTrialPeriod={false}
                                source="resubscribe_page"
                              />
                            </Col>
                            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24} style={{ display: 'flex' }}>
                              <PricePlanCard
                                plan='Complete'
                                price={currentPrices.find(p => p.plan === 'Complete' && p.period === newPeriod) as IPrice}
                                totalUsers={numberOfUsers}
                                currentPeriod={currentPeriod}
                                newPeriod={newPeriod}
                                currentPlan={newPlan}
                                showRevertingInfo={true}
                                isSignup={false}
                                isResubscribe={true}
                                onSelectPLan={() => changePlanHandler('Complete')}
                                isTrialPeriod={false}
                                source="resubscribe_page"
                              />
                            </Col>
                          </Row>
                        </>
                        }

                        <Divider />

                        <Row ref={billingInfoRef} style={{ scrollMarginTop: 80 }}
                          gutter={{
                            xs: 8,
                            sm: 16,
                            md: 24,
                            lg: 32,
                          }}
                        >
                          <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                            <Title level={4}>
                              <IntlMessages id="components.billingInfo.title" />
                            </Title>
                            <Paragraph type="secondary">
                              <IntlMessages id="components.billingInfo.text" />
                            </Paragraph>
                          </Col>
                          <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                            <BillingForm form={form} />
                          </Col>
                        </Row>

                        <Divider />

                        <Row gutter={{
                          xs: 8,
                          sm: 16,
                          md: 24,
                          lg: 32,
                        }}>
                          <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                            <Title level={4}>
                              <IntlMessages id="components.billingCouponCode.title" />
                            </Title>
                            <Paragraph type="secondary">
                              <IntlMessages id="components.billingCouponCode.text" />
                            </Paragraph>
                          </Col>
                          <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                            <BillingCouponCode/>
                          </Col>
                        </Row>

                        <Divider />
                        <Row gutter={{
                          xs: 8,
                          sm: 16,
                          md: 24,
                          lg: 32,
                        }}>
                          <Col span={24}>
                            <Form.Item style={{ textAlign: 'right' }}>
                              {customerPortalLink &&
                                <Button className="button-right" type="link" href={customerPortalLink} size='large'>
                                  <IntlMessages id="subscription.stripeCustomerPortalLink" />
                                </Button>
                              }
                              <Button className="button-right" size="large" type="primary" loading={submitLoader} htmlType="submit" disabled={submitLoader}>
                                <IntlMessages id="subscription.subscribe" />
                              </Button>
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                    }
                  </div>}
                  {isAdp && <div>
                    <IntlMessages id="components.billingInfo.title" />
                    <ADPMarketplaceBtn />
                  </div>}
                </div>
              </div>
            </div>
          </div>
        </main>
      </section>

      {/* Entity Selection Modal for Downgrade */}
      {planDowngradeSourceData && pendingPlan && (pendingPlan === 'Freemium' || pendingPlan === 'Core') && (
        <PlanDowngradeModal
          visible={showEntitySelectionModal}
          onCancel={handleEntitySelectionCancel}
          onSubmit={handleEntitySelectionSubmit}
          sourceData={{
            teams: planDowngradeSourceData.teams || [],
            locations: planDowngradeSourceData.locations || [],
            leaveTypes: planDowngradeSourceData.leaveTypes || [],
          }}
          downgradeType={pendingPlan === 'Freemium' ? 'to-freemium' : 'complete-to-core'}
          targetPlan={pendingPlan}
          currentPeriod={currentPeriod}
          endDatePeriod={stripeCurrentPeriodEnd ? format(new Date(Number(stripeCurrentPeriodEnd) * 1000), 'MMMM do yyyy', { locale: getDateFnsLocale(locale.locale) }) : undefined}
          numberOfActiveUsers={numberOfUsers}
          source="resubscribe_page"
        />
      )}
    </section>
  )
}

const SubscriptionPage = () => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  )
}

export default SubscriptionPage
